import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import StyledTable, { TableBody, TableHead, TableCell, TableRow } from 'components/global/Table';
import { Select, MenuItem, Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Pagination from '@mui/material/Pagination';

import TopBar from './partials/topBar';
import ClientSelect from '../global/ClientSelect';
import GlobalPanel from '../global/Panel';
import DataLoadingSpinner from '../global/DataLoadingSpinner';
import { fetchPaginatedFileShares } from '../../actions/resources';
import { getPreferenceTz } from 'utils/timezone';

const propTypes = {
  fetchPaginatedFileShares: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

function FilesFromClients({
  fetchPaginatedFileShares: fetchPaginatedFileSharesAction,
  intl,
}) {
  const [files, setFiles] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [sortBy, setSortBy] = useState('date-newest');

  const fetchList = (page, overrides = {}) => {
    const effectiveClientId =
      overrides.clientId !== undefined ? overrides.clientId : clientId;
    const effectiveSortBy =
      overrides.sortBy !== undefined ? overrides.sortBy : sortBy;
    setLoading(true);
    fetchPaginatedFileSharesAction(
      page,
      effectiveClientId,
      effectiveSortBy,
      (result, error) => {
        setLoading(false);
        if (!error) {
          setFiles(result.data);
          setPagination(result.pagination_info);
        }
      },
    );
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClientSelectChange = newClientId => {
    setClientId(newClientId);
    fetchList(1, { clientId: newClientId });
  };

  const handleSortChange = event => {
    const newSortBy = event.target.value;
    setSortBy(newSortBy);
    fetchList(1, { sortBy: newSortBy });
  };

  const renderRows = () => {
    const { formatDate } = intl;

    return files.map(file => (
      <TableRow key={file.id}>
        <TableCell>
          {formatDate(file.created_at, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone: getPreferenceTz(),
          })}
        </TableCell>
        <TableCell className="process-name">{file.user}</TableCell>
        <TableCell>
          {file.file_name.replace(/__([a-zA-Z0-9])+\./, '.')}
        </TableCell>
        <TableCell>
          <Button
            href={file.resource}
            className="flat-button-baby-blue process-action-button"
            download
            target="_blank"
          >{<FormattedMessage defaultMessage="View" />}</Button>
        </TableCell>
      </TableRow>
    ));
  };

  const renderTable = () => (
    <>
      <StyledTable>
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Date" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="Client" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="File" />
            </TableCell>
            <TableCell>
              <FormattedMessage defaultMessage="Action" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows()}</TableBody>
      </StyledTable>
      <div
        style={{ display: 'flex', justifyContent: 'center', marginTop: 30 }}
      >
        {pagination && pagination.total && pagination.total_items ? (
          <Pagination
            shape="rounded"
            count={pagination.total}
            page={pagination.current}
            onChange={(_, page) => fetchList(page)}
          />
        ) : (
          <noscript />
        )}
      </div>
    </>
  );

  const renderFilters = () => {
    const { formatMessage } = intl;

    return (
      <Grid container spacing={2} style={{ marginBottom: 30 }}>
        <Grid size="auto">
          <div style={{ width: 250 }}>
            <ClientSelect onChange={handleClientSelectChange} />
          </div>
        </Grid>
        <Grid size="auto">
          <Select value={sortBy} onChange={handleSortChange}>
            <MenuItem value="date-newest">
              {formatMessage({
                defaultMessage: 'Date - Newest First',
              })}
            </MenuItem>
            <MenuItem value="date-oldest">
              {formatMessage({
                defaultMessage: 'Date - Oldest First',
              })}
            </MenuItem>
          </Select>
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <TopBar activeTab="files_from_clients" />
      <GlobalPanel>
        {renderFilters()}
        {loading ? <DataLoadingSpinner /> : renderTable()}
      </GlobalPanel>
    </div>
  );
}

FilesFromClients.propTypes = propTypes;

export default injectIntl(
  connect(null, { fetchPaginatedFileShares })(FilesFromClients),
);
